import { Injectable } from '@angular/core';
import { Subscription, BehaviorSubject, throwError, Observable, of } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';

import { IPicklistOrderShipment } from '../../../picklist/interfaces/picklist.interface';
import { IShipmentFormData } from '../../interfaces/order-shipment.interface';
import { EnvironmentUtilService } from '../util';

import {
  IShipStationCarriers,
  IShipstationPackagesServices,
  IShipstationRates,
} from './../../../shipping/interfaces/shipstation.interface';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class ShipstationApiService {
  private ratesCache = new Map<string, Observable<IShipstationRates[]>>();

  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _loadingCarriers = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _loadingPackages = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _loadingServices = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _submittingShippingLabel = new BehaviorSubject<boolean>(
    <boolean>false,
  );
  private readonly _carriers = new BehaviorSubject<IShipStationCarriers[]>(
    <IShipStationCarriers[]>[],
  );
  private readonly _packages = new BehaviorSubject<IShipstationPackagesServices[]>(
    <IShipstationPackagesServices[]>[],
  );
  private readonly _services = new BehaviorSubject<IShipstationPackagesServices[]>(
    <IShipstationPackagesServices[]>[],
  );
  private readonly _rates = new BehaviorSubject<any>(<any>[]);
  loading$ = this._loading.asObservable();
  loadingCarriers$ = this._loadingCarriers.asObservable();
  loadingPackages$ = this._loadingPackages.asObservable();
  loadingServices$ = this._loadingServices.asObservable();
  carriers$ = this._carriers.asObservable();
  packages$ = this._packages.asObservable();
  services$ = this._services.asObservable();
  rates$ = this._rates.asObservable();
  submittingShippingLabel$ = this._submittingShippingLabel.asObservable();

  environment = this.environmentUtilService.getEnvironment();

  constructor(
    private apiService :ApiService,
    private environmentUtilService :EnvironmentUtilService,
  ) {}

  // Obsolete:
  getCarriers(params ?:{ [param :string] :string | string[] }) :Subscription {
    this._loadingCarriers.next(true);

    return this.apiService
      .get(`${this.environment.urlV2}/integration/shipping-carriers`, {
        params,
      })
      .subscribe({
        next: (response :any) => {
          this._carriers.next(response);
          this._loadingCarriers.next(false);
        },
        error: (error) => throwError(() => new Error(error)),
      });
  }

  getCarriersV2(params ?:{ [param :string] :string | string[] }) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/integration/shipping-carriers`,
      {
        params,
      },
    );
  }

  getPackages(params ?:{ [param :string] :string | string[] }) :Subscription {
    this._loadingPackages.next(true);

    return this.apiService
      .get(`${this.environment.urlV2}/integration/shipping-carriers/packages`, {
        params,
      })
      .pipe(finalize(() => this._loadingPackages.next(false)))
      .subscribe({
        next: (response :any) => {
          this._packages.next(response);
        },
        error: (error) => throwError(() => new Error(error)),
      });
  }

  getPackagesV2(params ?:{ [param :string] :string | string[] }) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/integration/shipping-carriers/packages`,
      {
        params,
      },
    );
  }

  // Obsolete: use getServicesV2
  getServices(params ?:{ [param :string] :string | string[] }) :Subscription {
    this._loadingServices.next(true);

    return this.apiService
      .get(`${this.environment.urlV2}/integration/shipping-carriers/services`, {
        params,
      })
      .pipe(finalize(() => this._loadingServices.next(false)))
      .subscribe({
        next: (response :any) => {
          this._services.next(response);
        },
        error: (error) => throwError(() => new Error(error)),
      });
  }

  getServicesV2(params ?:{ [param :string] :string | string[] }) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/integration/shipping-carriers/services`,
      {
        params,
      },
    );
  }

  getRates(params :any) :Observable<IShipstationRates[]> {
    const paramsKey = JSON.stringify(params);

    if (this.ratesCache.has(paramsKey)) {
      const cachedData = this.ratesCache.get(paramsKey);
      if (cachedData) return cachedData;
    }

    const request$ = this.apiService
      .post({
        path: `${this.environment.urlV2}/integration/shipping-rates`,
        body: params,
      })
      .pipe(
        tap((response :IShipstationRates[]) => {
          this._rates.next(response);
        }),
        shareReplay(1),
        catchError((error) => {
          // Handle error
          console.error('Error fetching rates:', error);
          this.ratesCache.delete(paramsKey);

          return of([]);
        }),
        tap({
          complete: () => this._loading.next(false),
        }),
      );

    this._loading.next(true);
    this.ratesCache.set(paramsKey, request$);

    return request$;
  }

  // getRates(params: any): Observable<IShipstationRates[]> {
  //   this._loading.next(true);

  //   return this.apiService
  //     .post({
  //       path: `${this.environment.urlV2}/integration/shipping-rates`,
  //       body: params,
  //     })
  //     .pipe(
  //       tap({
  //         next: (response: IShipstationRates[]) => {
  //           this._rates.next(response);
  //           this._loading.next(false);
  //         },
  //         error: () => {
  //           this._loading.next(false);
  //         },
  //       }),
  //     );
  // }

  createShippingLabel(payload :any) :Observable<IPicklistOrderShipment> {
    this._submittingShippingLabel.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV2}/integration/shipping-labels`,
        body: payload,
      })
      .pipe(
        tap({
          next: (response :IPicklistOrderShipment) => {
            this._submittingShippingLabel.next(false);

            return response;
          },
          error: () => {
            this._submittingShippingLabel.next(false);
          },
        }),
      );
  }

  getCarrierByParams(params ?:{
    [param :string] :string | string[];
  }) :Observable<any> {
    return this.apiService
      .get(`${this.environment.urlV2}/integration/shipping-carriers`, {
        params,
      })
      .pipe(
        tap({
          next: (response :any) => {
            return response;
          },
          error: (error) => throwError(() => new Error(error)),
        }),
      );
  }

  getServicesByParams(params ?:{
    [param :string] :string | string[];
  }) :Observable<any> {
    return this.apiService
      .get(`${this.environment.urlV2}/integration/shipping-carriers/services`, {
        params,
      })
      .pipe(
        tap({
          next: (response :any) => {
            return response;
          },
          error: (error) => throwError(() => new Error(error)),
        }),
      );
  }

  getPackagesByParams(params ?:{
    [param :string] :string | string[];
  }) :Observable<any> {
    return this.apiService
      .get(`${this.environment.urlV2}/integration/shipping-carriers/packages`, {
        params,
      })
      .pipe(
        tap({
          next: (response :any) => {
            return response;
          },
          error: (error) => throwError(() => new Error(error)),
        }),
      );
  }

  bulkCreateShippingLabel(payload :any) :Observable<IPicklistOrderShipment> {
    this._submittingShippingLabel.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV2}/integration/shipping-labels/bulk-create`,
        body: payload,
      })
      .pipe(
        tap({
          next: (response :IPicklistOrderShipment) => {
            this._submittingShippingLabel.next(false);

            return response;
          },
          error: () => {
            this._submittingShippingLabel.next(false);
          },
        }),
      );
  }

  bulkUpdateShippingLabel(payload :any) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV2}/integration/shipping-labels/bulk-update`,
      body: payload,
    });
  }

  getShipments(params :{ [param :string] :string | string[] }) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/inventory/ship-station-shipments`,
      {
        params,
      },
    );
  }

  getShipmentItems(shipstationShipmentId :number) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/inventory/ship-station-shipment-items/all/${shipstationShipmentId}`,
    );
  }

  createInvoiceFromShipment(shipstationShipmentId :number) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV2}/inventory/ship-station-shipments/${shipstationShipmentId}/invoice`,
    });
  }

  getLabelWithPackingSlip(
    salesOrderIds :string,
    packingSlipPageSize :string,
  ) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/pick-and-pack/picklists/export-labels-with-packing-slip`,
      {
        params: {
          salesOrderIds,
          packingSlipPageSize,
        },
      },
    );
  }

  getShipstationAddressText(shipping :any) :string {
    return `${shipping.name}, ${shipping.company}, ${shipping.street1}, ${
      shipping.street2 ? shipping.street2 + ', ' : ''
    }${shipping.city}, ${shipping.state}, ${shipping.postalCode}, ${
      shipping.country
    }`;
  }
}
